import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { Grid, Typography } from '@mui/material';
import { IPost } from '_interfaces/post.interface';
import { editableDraftPostAtom } from 'atoms';
import { DraftCard } from '../draft-card';
import { DraftCardMedia } from '../draft-card-media';
import { DraftCardMessage } from '../draft-card-message';
import UploadedAudioContent from 'components/uploaded-audio-content';
import UploadedFiles from 'components/uploaded-files';
import { useGetMeQuery } from 'redux-store/api/auth';
import { useGetAllProjectsQuery } from 'redux-store/api/projects';
import { DraftDeletionConfirmDialog } from '../draft-deletion-confirm-dialog';
import { getDraftDetails } from './utils';
import { IDraftsList } from './types';
import { styles } from './styles';

export const DraftsList = ({ drafts, onClose }: IDraftsList) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [draftToBeDeleted, setDraftToBeDeleted] = useState<IPost | null>(null);

  const setEditableDraft = useSetAtom(editableDraftPostAtom);

  const { data: userMe } = useGetMeQuery();
  const { data: allProjects, isLoading: isAllProjectsLoading } = useGetAllProjectsQuery(
    { profileId: userMe?.id },
    { skip: !userMe?.id }
  );

  const isOnProjectDashboardPage = () => {
    return pathname.includes('project-dashboard');
  };

  const handleCompletePost = (draft: IPost) => {
    setEditableDraft(draft);
    const onDashboard = isOnProjectDashboardPage();
    if (!onDashboard) navigate(`/project-dashboard/${draft.projectId}`);
    onClose();
  };

  const onComplete = (draft: IPost) => {
    handleCompletePost(draft);
  };

  const onDelete = (draft: IPost) => {
    setDraftToBeDeleted(draft);
  };

  return (
    <>
      <Grid sx={styles.cardsContainer}>
        {drafts.map((draft: IPost, index: number, arr: IPost[]) => {
            const {
              content,
              media,
              hasMedia,
              hasContent,
              hasMediaOrContent,
              postfix,
              project,
              uploads,
              completePrefix,
            } = getDraftDetails({ draft, allProjects });

            const isFirstOfProject =
              index === arr.findIndex((d) => Number(d.projectId) === Number(draft.projectId));

            const headerProps = isFirstOfProject
              ? {
                  isLoading: isAllProjectsLoading,
                  name: project.name,
                  location: project.location,
                  createdAt: project.createdAt,
                }
              : undefined;

            return (
              <DraftCard
                key={draft.id}
                headerProps={headerProps}
                footerProps={{
                  postfix,
                  isDraftActive: false,
                  completePrefix,
                  onComplete: () => onComplete(draft),
                  onDelete: () => onDelete(draft),
                }}
              >
                {hasMediaOrContent && (
                  <Grid>
                    {!isFirstOfProject && (
                      <Typography sx={styles.createdAtTypo}>{project.createdAt}</Typography>
                    )}
                    <Grid sx={styles.contentMediaContent}>
                      {hasMedia && <DraftCardMedia media={media} hasContent={hasContent} />}
                      {hasContent && <DraftCardMessage projectId={project.id} message={content} />}
                    </Grid>
                  </Grid>
                )}
                <UploadedAudioContent uploadedMultimedia={uploads} />
                <UploadedFiles type="activity_feed" uploads={uploads} />
              </DraftCard>
            );
          })}
      </Grid>
      {!!draftToBeDeleted && (
        <DraftDeletionConfirmDialog
          draft={draftToBeDeleted}
          onClose={() => {
            setDraftToBeDeleted(null);
          }}
        />
      )}
    </>
  );
};
